import React, { useContext, useEffect, useRef, useState } from 'react'
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import Search from 'Components/DesignSystem/Search/Search';
import Button from 'Components/DesignSystem/Button/Button';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { MenuItem } from 'types/DesignSystem/Menu';
import { Filter, Edit, RowAction } from 'Components/DesignSystem/Iconography/Icons';
import FilterSelectionMenu from './FilterSelectionMenu';
import ConfirmActionModal from 'Pages/GMS/CatalogManagement/modals/ConfirmActionModal';
import { toast } from 'Components/DesignSystem/Toast/useToast';

const SearchAndActions = () => {
    const menuRef = useRef<HTMLDivElement>(null);
    const rowActionsMenuRef = useRef<HTMLDivElement>(null);
    const [isRowActionsMenuOpen, setIsRowActionsMenuOpen] = useState(false);

    const {
        loading,
        tableSearchValue,
        onSearchTableData,
        isFilterOpen,
        setIsFilterOpen,
        isEditMode,
        handleEditClick,
        isSaving,
        setIsSaving,
        activeTab,
        attributeRowActions,
        attributeValueRowActions,
        categoryRowActions,
        saveChanges,
        discardChanges,
        bulkReviewAllAttributeValues,
        bulkUpdatePermission,
        updatePermission,
    } = useContext<MappingContextType>(MappingContext);

    const [isBulkReviewing, setIsBulkReviewing] = useState(false);
    const [showReviewAllConfirm, setShowReviewAllConfirm] = useState(false);

    // Get current tab's row actions
    const getCurrentRowActions = () => {
        switch (activeTab) {
            case 'attribute':
                return attributeRowActions;
            case 'attributevalue':
                return attributeValueRowActions;
            case 'category':
                return categoryRowActions;
            default:
                return null;
        }
    };

    const currentRowActions = getCurrentRowActions();

    const handleFilterClick = () => setIsFilterOpen((prev) => !prev);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await saveChanges();
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        discardChanges();
    };

    const handleRowActionsClick = () => {
        setIsRowActionsMenuOpen((prev) => !prev);
    };

    const handleMarkAsReviewed = async () => {
        if (currentRowActions?.markAsReviewed) {
            currentRowActions.markAsReviewed();
            setIsRowActionsMenuOpen(false);
        }
    };

    const handleBulkReviewAll = async () => {
        if (isBulkReviewing) return;
        setIsBulkReviewing(true);
        try {
            const result = await bulkReviewAllAttributeValues();
            if (result?.done && result?.updated_count === 0) {
                toast.info('No attribute values pending review');
            }
        } finally {
            setIsBulkReviewing(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (rowActionsMenuRef.current && !rowActionsMenuRef.current.contains(event.target as Node) && isRowActionsMenuOpen) {
                setIsRowActionsMenuOpen(false);
            }
        };

        if (isFilterOpen || isRowActionsMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isRowActionsMenuOpen, isFilterOpen]);

    // The DS Menu is presentational (mounted/unmounted by this component); its
    // items carry the legacy per-action testids so the Playwright contract in
    // okf/mapping/e2e.md § 4 is unchanged. Closing after an action stays in the
    // handlers, exactly as before.
    const rowActionsMenuItems: MenuItem[] = [
        {
            id: 'mark-as-reviewed',
            label: 'Mark as Reviewed',
            'data-testid': 'mark-as-reviewed-button',
            onClick: handleMarkAsReviewed,
        },
        ...(activeTab === 'category' && currentRowActions?.markAsConsidered
            ? [{
                id: 'consider-all',
                label: 'Consider All',
                'data-testid': 'consider-all-button',
                onClick: () => {
                    currentRowActions.markAsConsidered?.();
                    setIsRowActionsMenuOpen(false);
                },
            } satisfies MenuItem]
            : []),
    ];

    return (
        <div
            /* § 325b — the ROW declares the spacing. It used not to: each group
               hand-rolled its own `tw-mr-2`/`tw-pl-2`, which left every boundary at 8px
               EXCEPT `search → first action`, which had none at all. That was invisible
               only while the search was a fixed 384px inside a wider wrapper — the
               leftover slack was doing the job of a gap. Letting the search fill its
               wrapper (§ 325, so it can shrink in the side panel) removed the accident
               and the two sat flush. `tw-gap-2` is the same 8px the other boundaries
               already had, declared once where it cannot be forgotten by the next group
               added to this row. */
            className="tw-flex tw-items-center tw-gap-2 tw-h-12"
            ref={menuRef}
            data-testid="search-and-actions"
        >
            {/*
                Search Bar.

                § 325 — `tw-min-w-0` is the load-bearing half of `tw-flex-1`. A flex item
                defaults to `min-width: auto`, so it refuses to shrink below its content's
                min-content width; `flex-1` only ever GROWS it. With a fixed-width child
                this wrapper had a 384px floor, and since every other group in this row is
                `tw-flex-shrink-0`, the row's own minimum was ~707px (~877 in edit mode).
                That is what made the Mapping side panel scroll sideways when this module
                is opened from Catalog Management → Missing Mapping: the tables carry their
                own `overflow-x`, so this row was the only thing pushing on the panel.
            */}
            <div className="tw-flex-1 tw-min-w-0">
                <Search
                    value={tableSearchValue}
                    onChange={(value) => onSearchTableData(value)}
                    /* § 288 — the context's own fetch flag (`SET_LOADING` wraps the
                       load that puts `tableSearchValue` on the request as `search`).
                       No debounce term here: outside the attribute-value tab the load
                       effect is keyed on the raw value, so this flips immediately. */
                    loading={loading}
                    loadingLabel="Searching mappings"
                    placeholder="Search"
                    ariaLabel="Search"
                    /* § 325 — was a fixed `tw-w-96`. `tw-w-full` fills whatever the
                       wrapper allows; `tw-max-w-96` caps it at the same 384px it used to
                       be, so on the full `/gms/mapping` page — where the wrapper is wider
                       than 384 — this renders identically to before. The change only does
                       anything where the space is too small, which is where the bug was. */
                    className="tw-w-full tw-max-w-96"
                    data-testid="mapping-search-input"
                />
            </div>

            {/* Bulk Review All Button - Only visible on Attribute Values tab with bulkupdate permission */}
            {activeTab === 'attributevalue' && bulkUpdatePermission && (
                <div className="tw-flex tw-items-center tw-flex-shrink-0">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowReviewAllConfirm(true)}
                        disabled={isBulkReviewing || isEditMode}
                        loading={isBulkReviewing}
                        data-testid="bulk-review-all-button"
                    >
                        {isBulkReviewing ? 'Reviewing...' : 'Review All'}
                    </Button>
                </div>
            )}

            {/* Row Actions */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 tw-relative" ref={rowActionsMenuRef}>
                <Button
                    variant="subtle"
                    size="sm"
                    onClick={handleRowActionsClick}
                    disabled={!currentRowActions?.hasSelection}
                    aria-haspopup="menu"
                    aria-expanded={isRowActionsMenuOpen}
                    icon={<RowAction />}
                    data-testid="row-actions-button"
                >
                    Row Actions
                </Button>

                {/* Row Actions Dropdown Menu */}
                {isRowActionsMenuOpen && isEditMode && currentRowActions?.hasSelection && (
                    <Menu
                        items={rowActionsMenuItems}
                        variant="compact"
                        className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-z-50 tw-min-w-max"
                        data-testid="row-actions-menu"
                    />
                )}
            </div>

            {/* Filter Menu Button — the testid predates this page (shared MenuButton
                hardcoded it); kept verbatim because it is the documented contract. */}
            <div className="tw-flex tw-items-center tw-flex-shrink-0">
                <Button
                    variant="subtle"
                    size="sm"
                    onClick={handleFilterClick}
                    aria-haspopup="true"
                    aria-expanded={isFilterOpen}
                    icon={<Filter />}
                    data-testid="category-relevancy-menu-btn"
                >
                    Filter
                </Button>
                <FilterSelectionMenu />
            </div>

            {/* Edit Mode Actions */}
            {isEditMode ? (
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                        data-testid="cancel-button"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving}
                        loading={isSaving}
                        className="tw-min-w-[80px]"
                        data-testid="save-button"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            ) : (
                <div className="tw-flex-shrink-0">
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={handleEditClick}
                        disabled={!updatePermission}
                        title={updatePermission ? undefined : "You don't have permission to edit mappings"}
                        icon={<Edit />}
                        data-testid="edit-button"
                    >
                        Edit
                    </Button>
                </div>
            )}

            {/* Confirmation before bulk-reviewing every attribute value */}
            <ConfirmActionModal
                isOpen={showReviewAllConfirm}
                onClose={() => setShowReviewAllConfirm(false)}
                onConfirm={async () => {
                    await handleBulkReviewAll();
                    setShowReviewAllConfirm(false);
                }}
                title="Review All Attribute Values"
                message="This will mark every attribute value as Reviewed, across all attributes. Do you want to continue?"
                confirmLabel="Review All"
                isLoading={isBulkReviewing}
            />
        </div>
    );
};

export default SearchAndActions;